import {
  hasBulge, segmentCount, bulgeArray, segmentEnds, pointOnSegment, segMidpoint, bulgeArcPoints, tessellatePolyline,
} from './bulge.js'

// ── Class colors (keyed by API name) ─────────────────────────────────────────
export const CLASS_COLORS = {
  'Walls':                '#2196f3',
  'Columns':              '#00bcd4',
  'Doors':                '#f44336',
  'Windows':              '#ff9800',
  'Stairs':               '#9c27b0',
  'Railings':             '#64b5f6',
  'Elevators':            '#512da8',
  'Mullions':             '#ffc107',
  'Toilet seat':          '#c8a87a',
  'Interior Design':      '#ad1457',
  'Furniture':            '#aed581',
  'Untagged':             '#bdbdbd',
  'Flood':                '#ff7043',
  'Outdoor Flood':        '#6d4c41',
  'Zones':                '#10b981',
  'background':           '#d1d5db',
  '0':                    '#6b7280',
  'General':              '#6b7280',
  'Door Lines':           '#fb923c',
  'Main Entrance Q-Line': '#aeea00',
  'Door Q-Line':          '#43a047',
  'Egress Q-Line':        '#fdd835',
  'Core Q-Line':          '#e53935',
  'Regular Q-Line':       '#bdbdbd',
}

// ── Display names ─────────────────────────────────────────────────────────────
const DISPLAY_NAME_MAP = {
  'Walls':           'qbiq_walls',
  'Doors':           'qbiq_doors',
  'Columns':         'qbiq_columns',
  'Windows':         'qbiq_windows',
  'Stairs':          'qbiq_stairs',
  'Railings':        'qbiq_railings',
  'Elevators':       'qbiq_elevators',
  'Mullions':        'qbiq_mullions',
  'Toilet seat':     'qbiq_toilet_seat',
  'Furniture':       'qbiq_furniture',
  'Zones':           'qbiq_zones',
  'Interior Design': 'qbiq_interior_design',
  'Flood':           'qbiq_flood',
  'Outdoor Flood':   'qbiq_outdoor_flood',
  'General':         'qbiq_general',
}
export function toDisplayName(n) { return DISPLAY_NAME_MAP[n] || n }

// ── Q-Line parent map ─────────────────────────────────────────────────────────
export const QLINE_PARENT = {
  'Door Lines':           'Doors',
  'Main Entrance Q-Line': 'Doors',
  'Door Q-Line':          'Doors',
  'Egress Q-Line':        'Doors',
  'Core Q-Line':          'Walls',
  'Regular Q-Line':       'Walls',
}
const QLINE_NAMES = new Set(Object.keys(QLINE_PARENT))
export const isQLine = (cls) => QLINE_NAMES.has(cls)

// ── Layer order (matches reference UI) ───────────────────────────────────────
export const QBIQ_LAYER_NAMES = [
  '0',
  'Walls', 'Columns', 'Doors', 'Windows', 'Stairs', 'Railings',
  'Elevators', 'Mullions', 'Toilet seat', 'Interior Design', 'Furniture', 'Untagged',
  'Flood', 'Outdoor Flood',
]

export const QLINE_ORDER = [
  'Main Entrance Q-Line', 'Door Q-Line', 'Egress Q-Line', 'Core Q-Line', 'Regular Q-Line',
]

// Layers available for drawing tool
export const DRAW_LAYER_NAMES = [
  'Walls', 'Columns', 'Doors', 'Windows', 'Stairs', 'Railings',
  'Elevators', 'Mullions', 'Toilet seat', 'Interior Design', 'Furniture',
  'Main Entrance Q-Line', 'Door Q-Line', 'Egress Q-Line', 'Core Q-Line', 'Regular Q-Line',
  'Flood', 'Outdoor Flood',
]

// ── Post-process: join + close Columns LINE entities into LWPOLYLINEs ─────────
export function processColumnEntities(manifest, tol = 0.001) {
  if (!manifest?.length) return manifest

  // Separate Columns LINE entities from everything else
  const colLines = []
  const other = []
  for (const e of manifest) {
    if (e.layer === 'Columns' && e.type === 'LINE' && e.start && e.end) colLines.push(e)
    else other.push(e)
  }

  // Close existing LWPOLYLINEs in Columns
  const base = other.map(e =>
    e.layer === 'Columns' && e.type === 'LWPOLYLINE' ? { ...e, closed: true } : e
  )

  if (!colLines.length) return base

  const eq = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]) < tol
  const used = new Array(colLines.length).fill(false)
  const chains = []

  for (let i = 0; i < colLines.length; i++) {
    if (used[i]) continue
    used[i] = true
    const chain = [[...colLines[i].start], [...colLines[i].end]]

    let extended = true
    while (extended) {
      extended = false
      for (let j = 0; j < colLines.length; j++) {
        if (used[j]) continue
        const s = colLines[j].start, e = colLines[j].end
        const head = chain[0], tail = chain[chain.length - 1]
        if      (eq(tail, s)) { chain.push([...e]);       used[j] = true; extended = true }
        else if (eq(tail, e)) { chain.push([...s]);       used[j] = true; extended = true }
        else if (eq(head, e)) { chain.unshift([...s]);    used[j] = true; extended = true }
        else if (eq(head, s)) { chain.unshift([...e]);    used[j] = true; extended = true }
      }
    }
    chains.push({ chain, ref: colLines[i] })
  }

  const newPolys = chains.map(({ chain, ref }) => {
    const closed = chain.length > 2 && eq(chain[0], chain[chain.length - 1])
    const points = closed ? chain.slice(0, -1) : chain
    const { start: _s, end: _e, ...rest } = ref
    return { ...rest, type: 'LWPOLYLINE', points, closed }
  })

  return [...base, ...newPolys]
}

// ── Layer list from manifest ──────────────────────────────────────────────────
export function getLayers(manifest) {
  if (!manifest?.length) return []
  const seen = new Set(), result = []
  for (const e of manifest) {
    // Normalize layer to string — DXF layer "0" can arrive as number 0 from JSON
    const layer = e.layer != null ? String(e.layer) : null
    if (layer == null) continue
    if (!seen.has(layer)) { seen.add(layer); result.push(layer) }
  }
  return result.sort()
}

// ── Protected layers (Interior Design / Furniture) ───────────────────────────────
// Entities the user pinned during Preprocess: classifier output is ignored
// for them. Returns the input array untouched when nothing is protected.
export function applyProtectedLayers(entities, protectedMap) {
  if (!entities?.length || !protectedMap || !Object.keys(protectedMap).length) return entities
  return entities.map(e => {
    const cls = protectedMap[e.handle]
    return cls ? { ...e, layer: cls, isProtected: true, clientLayer: e.clientLayer ?? e.layer } : e
  })
}

// A LINE has no vertex or bulge of its own — adding a vertex (via the midpoint
// diamond / "Add Vertex") or bowing it into an arc ("Convert to Arc") both have
// to promote it into an LWPOLYLINE to hold that.
export function applyLineVertexEdit(p, newPoints, newBulge) {
  const hasCurve = newBulge?.some(hasBulge)
  if (newPoints.length <= 2 && !hasCurve) return { ...p, start: newPoints[0], end: newPoints[1] }
  const { start: _s, end: _e, ...rest } = p
  return { ...rest, type: 'LWPOLYLINE', points: newPoints, bulge: newBulge ?? [], closed: false }
}

// Strip display-only protection props before persisting an entity to edit state
export function stripProtection(e) {
  if (!e?.isProtected) return e
  const { isProtected: _p, clientLayer, ...rest } = e
  return { ...rest, layer: clientLayer ?? e.layer }
}

// Sublayer key for a handle reclassified via split action or "move to layer"
// (App.jsx classifiedManifest/handleMoveToLayer/handleApplySplit) — moving it
// to a new sublayer "{orig}_{class}" so hiding the source layer's group
// doesn't also hide these (now reclassified) entities.
export function reclassifiedLayerName(originalLayerName, qbiqClass) {
  return `${originalLayerName}_${qbiqClass}`
}

// ── Original layer → qbiq class + confidence stats ───────────────────────────
export function getOriginalLayerStats(classifiedManifest) {
  if (!classifiedManifest?.length) return {}
  const map = {}
  for (const e of classifiedManifest) {
    if (e.isUserDrawn || e.isTransferred) continue
    const orig = e.original_layer_name || e.layer
    if (!map[orig]) map[orig] = { classes: {}, scores: [] }
    map[orig].classes[e.layer] = (map[orig].classes[e.layer] || 0) + 1
    if (e.score != null) map[orig].scores.push(e.score)
  }
  return Object.fromEntries(Object.entries(map).map(([name, { classes, scores }]) => {
    const sorted = Object.entries(classes).sort((a, b) => b[1] - a[1])
    const avgScore = scores.length
      ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length * 100)
      : null
    return [name, { bestClass: sorted[0]?.[0], avgScore }]
  }))
}

// ── Build classified groups ───────────────────────────────────────────────────
export function buildClassifiedGroups(manifest) {
  if (!manifest?.length) return []
  const mainMap = {}, qlineMap = {}
  for (const e of manifest) {
    const cls  = e.layer != null ? String(e.layer) || '0' : '0'
    const orig = e.original_layer_name || (e.layer != null ? String(e.layer) : null) || '?'
    const map  = QLINE_NAMES.has(cls) ? qlineMap : mainMap
    if (!map[cls]) map[cls] = { entities: [], sublayerMap: {} }
    map[cls].entities.push(e)
    if (!map[cls].sublayerMap[orig]) map[cls].sublayerMap[orig] = []
    map[cls].sublayerMap[orig].push(e)
  }
  const qlineGroups = Object.entries(qlineMap).map(([qbiqClass, { entities, sublayerMap }]) => ({
    qbiqClass, displayName: qbiqClass,
    color: CLASS_COLORS[qbiqClass] || '#9ca3af',
    avgScore: avgScoreOf(entities), count: entities.length, isQLine: true,
    sublayers: makeSublayers(sublayerMap),
  }))
  return Object.entries(mainMap).map(([qbiqClass, { entities, sublayerMap }]) => ({
    qbiqClass, displayName: toDisplayName(qbiqClass),
    color: CLASS_COLORS[qbiqClass] || '#9ca3af',
    avgScore: avgScoreOf(entities), count: entities.length,
    sublayers: makeSublayers(sublayerMap),
    qlineGroups: qlineGroups.filter(q => QLINE_PARENT[q.qbiqClass] === qbiqClass),
    isProtectedClass: entities.length > 0 && entities.every(e => e.isProtected),
  }))
}

function makeSublayers(sublayerMap) {
  return Object.entries(sublayerMap).map(([name, ents]) => ({
    name, count: ents.length, avgScore: avgScoreOf(ents), probs: avgProbsOf(ents),
  }))
}
function avgScoreOf(entities) {
  const s = entities.map(e => e.score).filter(s => s != null)
  return s.length ? Math.round(s.reduce((a,b)=>a+b,0)/s.length*100) : null
}
function avgProbsOf(entities) {
  const sums = {}; let n = 0
  for (const e of entities) {
    if (!e.class_probabilities) continue; n++
    for (const [c,p] of Object.entries(e.class_probabilities)) sums[c] = (sums[c]||0)+p
  }
  if (!n) return {}
  return Object.fromEntries(Object.entries(sums).map(([c,s])=>[c,Math.round(s/n*100)]))
}

export function confidenceColor(p) {
  if (p==null) return '#9ca3af'; if (p>=90) return '#16a34a'; if (p>=75) return '#ca8a04'; return '#dc2626'
}

// ── Bounds ────────────────────────────────────────────────────────────────────
export function computeBounds(manifest) {
  let minX=Infinity,minY=Infinity,maxX=-Infinity,maxY=-Infinity
  for (const e of manifest) {
    if (e.type==='LINE'&&e.start&&e.end){
      minX=Math.min(minX,e.start[0],e.end[0]);maxX=Math.max(maxX,e.start[0],e.end[0])
      minY=Math.min(minY,e.start[1],e.end[1]);maxY=Math.max(maxY,e.start[1],e.end[1])
    } else if (e.type==='CIRCLE'&&e.center){
      minX=Math.min(minX,e.center[0]-e.radius);maxX=Math.max(maxX,e.center[0]+e.radius)
      minY=Math.min(minY,e.center[1]-e.radius);maxY=Math.max(maxY,e.center[1]+e.radius)
    } else if (e.type==='LWPOLYLINE'&&e.points?.length){
      for (const [x,y] of e.points){minX=Math.min(minX,x);maxX=Math.max(maxX,x);minY=Math.min(minY,y);maxY=Math.max(maxY,y)}
    }
  }
  return {minX,minY,maxX,maxY}
}

// ── Color helpers ─────────────────────────────────────────────────────────────
export function layerNameColor(name) {
  if (name == null || name === '') return '#888888'
  name = String(name)
  let h = 5381
  for (let i=0;i<name.length;i++) h = Math.imul(h^name.charCodeAt(i),1664525)+1013904223
  return `hsl(${(h>>>0)%360},${55+((h>>>8)%25)}%,${38+((h>>>16)%20)}%)`
}

// ── Geometry ──────────────────────────────────────────────────────────────────
export function distPointToSegment(px,py,ax,ay,bx,by){
  const dx=bx-ax,dy=by-ay,l=dx*dx+dy*dy
  if(!l) return Math.hypot(px-ax,py-ay)
  const t=Math.max(0,Math.min(1,((px-ax)*dx+(py-ay)*dy)/l))
  return Math.hypot(px-(ax+t*dx),py-(ay+t*dy))
}

// Distance from (px,py) to the arc/segment p1→p2 with bulge b.
export function distPointToArcSegment(px, py, p1, p2, b) {
  const pts = [p1, ...bulgeArcPoints(p1, p2, b)]
  return Math.min(...pts.slice(1).map((q, i) => distPointToSegment(px, py, pts[i][0], pts[i][1], q[0], q[1])))
}

// Inserts a vertex at fraction f along segment `si` (default: its midpoint).
// An arc splits into two arcs whose sweeps add up to the original.
export function insertVertexAtSegment(points, bulge, si, closed = false, f = 0.5) {
  const bArr = bulgeArray(points, bulge, closed)
  const [p1, p2] = segmentEnds(points, si)
  const b = bArr[si], q = Math.atan(b)
  const pt = pointOnSegment(p1, p2, b, f)
  return {
    points: [...points.slice(0, si + 1), pt, ...points.slice(si + 1)],
    bulge: [...bArr.slice(0, si), Math.tan(f * q), Math.tan((1 - f) * q), ...bArr.slice(si + 1)],
  }
}

// Removes vertex `vi`; the two segments meeting there merge into one straight
// segment. Returns null when too few vertices would remain.
export function removeVertex(points, bulge, vi, closed) {
  const n = points.length
  if (n <= (closed ? 3 : 2)) return null
  const bArr = bulgeArray(points, bulge, closed)
  const newPoints = points.filter((_, i) => i !== vi)
  let newBulge
  if (!closed && vi === 0) newBulge = bArr.slice(1)
  else if (!closed && vi === n - 1) newBulge = bArr.slice(0, -1)
  else if (vi === 0) newBulge = [...bArr.slice(1, n - 1), 0]
  else newBulge = [...bArr.slice(0, vi - 1), 0, ...bArr.slice(vi + 1)]
  return { points: newPoints, bulge: newBulge }
}

// Right-click "Convert to Arc" / "Convert to Line" on segment `si`.
export function toggleSegmentArc(points, bulge, si, closed) {
  const bArr = bulgeArray(points, bulge, closed)
  bArr[si] = hasBulge(bArr[si]) ? 0 : 0.5
  return bArr
}

// Grip under (wx,wy): a vertex first, then a segment midpoint diamond
// ('arc' for a bulged segment, 'midpoint' for a straight one).
export function hitPolylineGrip(points, bulge, closed, wx, wy, tol) {
  const vi = points.findIndex(([x, y]) => Math.hypot(wx - x, wy - y) <= tol)
  if (vi !== -1) return { mode: 'vertex', idx: vi }
  for (let si = 0; si < segmentCount(points, closed); si++) {
    const [p1, p2] = segmentEnds(points, si), b = bulge?.[si] || 0
    const [mx, my] = segMidpoint(p1, p2, b)
    if (Math.hypot(wx - mx, wy - my) <= tol) return { mode: hasBulge(b) ? 'arc' : 'midpoint', idx: si }
  }
  return null
}

// Nearest point on the drawn polyline (arcs included) within tol, as
// { si, f, pt } — f is kept away from the segment ends so a vertex inserted
// there never lands on an existing one.
export function nearestSegmentPoint(points, bulge, closed, wx, wy, tol) {
  const SAMPLES = 64, F_MIN = 0.05, F_MAX = 0.95
  let best = null, bestD = tol
  for (let si = 0; si < segmentCount(points, closed); si++) {
    const [p1, p2] = segmentEnds(points, si), b = bulge?.[si] || 0
    const fs = hasBulge(b)
      ? Array.from({ length: SAMPLES + 1 }, (_, k) => F_MIN + (F_MAX - F_MIN) * k / SAMPLES)
      : [Math.max(F_MIN, Math.min(F_MAX, ((wx - p1[0]) * (p2[0] - p1[0]) + (wy - p1[1]) * (p2[1] - p1[1])) / ((p2[0] - p1[0]) ** 2 + (p2[1] - p1[1]) ** 2 || 1)))]
    for (const f of fs) {
      const pt = pointOnSegment(p1, p2, b, f)
      const d = Math.hypot(wx - pt[0], wy - pt[1])
      if (d < bestD) { bestD = d; best = { si, f, pt } }
    }
  }
  return best
}

// Splits a polyline at vertex `vi` (right-click "Split" action). An open
// polyline breaks into two pieces sharing that vertex as their common
// endpoint; a closed one instead just opens the loop there — rotating the
// point/bulge arrays so `vi` becomes the new seam, since flipping `closed`
// off without rotating would always break the same fixed seam regardless of
// which vertex was clicked.
export function splitPolylineAtVertex(points, bulge, vi, closed) {
  const bArr = bulgeArray(points, bulge, closed)
  if (closed) {
    const rotPts   = [...points.slice(vi), ...points.slice(0, vi), points[vi]]
    const rotBulge = [...bArr.slice(vi), ...bArr.slice(0, vi)]
    return [{ points: rotPts, bulge: rotBulge, closed: false }]
  }
  return [
    { points: points.slice(0, vi + 1), bulge: bArr.slice(0, vi), closed: false },
    { points: points.slice(vi),        bulge: bArr.slice(vi),    closed: false },
  ]
}

// Reversing an arc's direction flips its bulge sign.
const reverseBulge = bulge => [...bulge].reverse().map(b => (b ? -b : 0))

// Greedy end-to-end join of { pts, bulge } chains into one open polyline,
// or null when some chain doesn't touch the rest within tol.
export function joinPolylines(chains, tol) {
  const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1])
  const segs = chains.map(c => ({ pts: c.pts, bulge: bulgeArray(c.pts, c.bulge, false), used: false }))
  const points = [...segs[0].pts], bulge = [...segs[0].bulge]
  segs[0].used = true
  let changed = true
  while (changed) {
    changed = false
    for (const s of segs.filter(x => !x.used)) {
      const head = points[0], tail = points[points.length - 1]
      const sH = s.pts[0], sT = s.pts[s.pts.length - 1]
      const revPts = [...s.pts].reverse(), revBulge = reverseBulge(s.bulge)
      if      (dist(tail, sH) <= tol) { points.push(...s.pts.slice(1));        bulge.push(...s.bulge) }
      else if (dist(tail, sT) <= tol) { points.push(...revPts.slice(1));       bulge.push(...revBulge) }
      else if (dist(head, sT) <= tol) { points.unshift(...s.pts.slice(0, -1)); bulge.unshift(...s.bulge) }
      else if (dist(head, sH) <= tol) { points.unshift(...revPts.slice(0, -1)); bulge.unshift(...revBulge) }
      else continue
      s.used = changed = true
    }
  }
  return segs.every(s => s.used) ? { points, bulge } : null
}

// One entity per segment: straight segments become LINEs, arcs stay
// single-segment bulged polylines (there's no ARC entity type here).
export function explodePolyline(e) {
  const bArr = bulgeArray(e.points, e.bulge, e.closed)
  return bArr.map((b, si) => {
    const [a, c] = segmentEnds(e.points, si).map(p => [p[0], p[1]])
    return hasBulge(b)
      ? { type: 'LWPOLYLINE', points: [a, c], bulge: [b], closed: false }
      : { type: 'LINE', start: a, end: c }
  })
}

// Vertex-editable view of a LINE or LWPOLYLINE, or null for anything else.
export function editableShape(e) {
  if (e.type === 'LINE' && e.start && e.end) return { points: [e.start, e.end], bulge: [0], closed: false }
  if (e.type === 'LWPOLYLINE' && e.points?.length) {
    const duplicateEnd = e.closed && e.points.length > 3 && e.points[0][0] === e.points.at(-1)[0] && e.points[0][1] === e.points.at(-1)[1]
    const points = duplicateEnd ? e.points.slice(0, -1) : e.points
    return { points, bulge: (e.bulge || []).slice(0, points.length), closed: !!e.closed }
  }
  return null
}

// Entity as it should be drawn while its vertices are being dragged.
export function liveVertexEntity(e, ve) {
  if (ve?.handle !== e.handle) return e
  if (e.type === 'LINE') return applyLineVertexEdit(e, ve.points, ve.bulge)
  return { ...e, points: ve.points, bulge: ve.bulge ?? e.bulge }
}

// ── Canvas visibility / selectability ─────────────────────────────────────────
// Shared by the render pass and every selection path so they can't disagree
// about which entities exist on screen.
export const normLayer = l => (l != null ? String(l) : null)
const bypassesVisibility = (e, av) => e.isUserDrawn && av !== 'classified'

// Classified view keys entities by their sublayer (original DXF layer); protected ones by class.
export function layerVisibilityKey(e, av) {
  const raw = normLayer(e.layer)
  return av === 'classified' && !e.isProtected ? (normLayer(e.original_layer_name) ?? raw) : raw
}

// The eye on a class group hides that class only. The source-layer key above is shared by every class a
// layer splits into, so hiding it would also hide that layer's entities classified as something else.
export const hideClassKey = cls => `\u0000hide-class:${cls}`

// The next visible-layers set after clicking a class group's eye. `allKeys` are its source-layer keys;
// `allVis` is whether the group is fully shown now. Pinned classes (Interior Design, Furniture) are keyed
// by class name and keep the plain key toggle. Never infer "pinned" from the names: a DXF layer can be
// called "0", the same as the class.
export function toggleClassVisibility(prev, cls, allKeys, allVis, isProtectedClass) {
  const next = new Set(prev)
  if (isProtectedClass) {
    for (const k of allKeys) allVis ? next.delete(k) : next.add(k)
  } else if (allVis) {
    next.add(hideClassKey(cls))
  } else {
    next.delete(hideClassKey(cls))
    allKeys.forEach(k => next.add(k))
  }
  return next
}

export function isEntityVisible(e, vl, av) {
  if (bypassesVisibility(e, av)) return true
  if (!vl.has(layerVisibilityKey(e, av))) return false
  return av !== 'classified' || !!e.isProtected || !vl.has(hideClassKey(normLayer(e.layer)))
}

// Isolate keys in the classified view: a class is its own name; a sublayer row is class + source layer, so
// isolating "EXTWALLS" under Windows leaves that layer's entities in other classes dimmed, and isolating
// the class "0" doesn't pick up entities that merely sit on the DXF layer called "0".
export const soloSubKey = (cls, layer) => `\u0000solo-sub:${cls}\u0001${layer}`

export function isEntityInSolo(e, sl, av) {
  if (!sl?.size || bypassesVisibility(e, av)) return true
  const cls = normLayer(e.layer)
  return sl.has(cls) || (av === 'classified' && !e.isProtected && sl.has(soloSubKey(cls, normLayer(e.original_layer_name))))
}

export function isEntitySelectable(e, vl, sl, av) {
  return isEntityVisible(e, vl, av) && isEntityInSolo(e, sl, av)
}

const FLOOD_LAYERS = new Set(['Flood', 'Outdoor Flood'])

// Which right-click menu actions apply. Flood polygons stay closed and
// straight-edged: their fill and area math skip open shapes and ignore arcs.
export function vertexMenuActions({ entity, hitVertex, hitMidpoint }) {
  const isPoly = entity.type === 'LWPOLYLINE', isLine = entity.type === 'LINE'
  const onMidpoint = hitMidpoint !== null, isFlood = FLOOD_LAYERS.has(entity.layer)
  const canConvert = (isPoly || isLine) && onMidpoint && !isFlood
  return {
    canRemove: isPoly && hitVertex !== null,
    canSplit: !isFlood && (isPoly ? hitVertex !== null || onMidpoint : isLine && onMidpoint),
    canConvert,
    isArc: canConvert && hasBulge(entity.bulge?.[hitMidpoint]),
  }
}

// Closest point ON a segment to (wx,wy), returned as [x,y]
export function closestPointOnSegment(wx,wy,ax,ay,bx,by){
  const dx=bx-ax,dy=by-ay,l=dx*dx+dy*dy
  if(!l) return [ax,ay]
  const t=Math.max(0,Math.min(1,((wx-ax)*dx+(wy-ay)*dy)/l))
  return [ax+t*dx, ay+t*dy]
}

// Intersection point of two line segments, or null if they don't intersect
export function segmentIntersectionPoint(p1,p2,p3,p4){
  const[x1,y1]=p1,[x2,y2]=p2,[x3,y3]=p3,[x4,y4]=p4
  const d=(x1-x2)*(y3-y4)-(y1-y2)*(x3-x4)
  if(Math.abs(d)<1e-10) return null
  const t=((x1-x3)*(y3-y4)-(y1-y3)*(x3-x4))/d
  const u=-((x1-x2)*(y1-y3)-(y1-y2)*(x1-x3))/d
  if(t<0||t>1||u<0||u>1) return null
  return [x1+t*(x2-x1), y1+t*(y2-y1)]
}

// Centroid of a closed polygon
export function polygonCentroid(points){
  if(!points?.length) return null
  let cx=0,cy=0,area=0
  for(let i=0;i<points.length;i++){
    const[x0,y0]=points[i],[x1,y1]=points[(i+1)%points.length]
    const cross=x0*y1-x1*y0
    area+=cross; cx+=(x0+x1)*cross; cy+=(y0+y1)*cross
  }
  area/=2
  if(Math.abs(area)<1e-10) return null
  return [cx/(6*area), cy/(6*area)]
}

// Perpendicular foot from point P to segment AB (null if foot lands outside the segment)
export function perpFoot(px,py,ax,ay,bx,by){
  const dx=bx-ax,dy=by-ay,l=dx*dx+dy*dy
  if(!l) return null
  const t=((px-ax)*dx+(py-ay)*dy)/l
  if(t<0||t>1) return null
  return [ax+t*dx, ay+t*dy]
}
export function findEntityAtPoint(manifest,wx,wy,tol){
  let best=null,bestD=tol
  for (const e of manifest){
    let d=Infinity
    if (e.type==='LINE'&&e.start&&e.end) d=distPointToSegment(wx,wy,e.start[0],e.start[1],e.end[0],e.end[1])
    else if (e.type==='CIRCLE'&&e.center) d=Math.abs(Math.hypot(wx-e.center[0],wy-e.center[1])-e.radius)
    else if (e.type==='LWPOLYLINE'&&e.points?.length>1){
      for (let si=0;si<segmentCount(e.points,e.closed);si++) d=Math.min(d,distPointToArcSegment(wx,wy,...segmentEnds(e.points,si),e.bulge?.[si]))
    }
    if (d<bestD){bestD=d;best=e}
  }
  return best
}
// Ray-casting point-in-polygon (for closed polylines that surround the box)
function ptInPoly(px,py,pts){
  let inside=false
  for(let i=0,j=pts.length-1;i<pts.length;j=i++){
    const[xi,yi]=pts[i],[xj,yj]=pts[j]
    if(((yi>py)!==(yj>py))&&px<(xj-xi)*(py-yi)/(yj-yi)+xi)inside=!inside
  }
  return inside
}

const MAX_GRID_DIM=256          // cells per axis, so the grid stays bounded whatever the units
const MAX_CELLS_PER_ENTITY=1024 // above this, binning an entity costs more than just scanning it

// Axis-aligned bbox of an entity, or null when it has no usable one (unknown type, or arc bulges
// that can swing outside the vertex bbox) — those go to the always-scanned fallback list.
function entityBBox(e){
  if(e.type==='LINE'&&e.start&&e.end)
    return [Math.min(e.start[0],e.end[0]),Math.min(e.start[1],e.end[1]),
            Math.max(e.start[0],e.end[0]),Math.max(e.start[1],e.end[1])]
  if(e.type==='CIRCLE'&&e.center){
    const r=e.radius||0
    return [e.center[0]-r,e.center[1]-r,e.center[0]+r,e.center[1]+r]
  }
  if(e.type==='LWPOLYLINE'&&e.points?.length){
    if(e.bulge?.some(b=>b&&Math.abs(b)>0.001))return null
    let x0=Infinity,y0=Infinity,x1=-Infinity,y1=-Infinity
    for(const[x,y]of e.points){if(x<x0)x0=x;if(x>x1)x1=x;if(y<y0)y0=y;if(y>y1)y1=y}
    return [x0,y0,x1,y1]
  }
  if(e.position)return [e.position[0],e.position[1],e.position[0],e.position[1]]
  return null
}

// Cell size follows the drawing's own geometry: DXF coordinates may be mm, m or inches, and a fixed
// size either collapses the grid into one bucket or explodes it into millions of cells. Sizing a
// cell to the mean entity keeps insertions ~O(1) each; the extent floor bounds the grid overall.
function deriveCellSize(boxed){
  let x0=Infinity,y0=Infinity,x1=-Infinity,y1=-Infinity,sizeSum=0
  for(const[,b]of boxed){
    if(b[0]<x0)x0=b[0];if(b[1]<y0)y0=b[1];if(b[2]>x1)x1=b[2];if(b[3]>y1)y1=b[3]
    sizeSum+=Math.max(b[2]-b[0],b[3]-b[1])
  }
  const extent=Math.max(x1-x0,y1-y0)
  if(!(extent>0))return 1
  return Math.max(sizeSum/boxed.length,extent/MAX_GRID_DIM)
}

export function buildSpatialIndex(entities){
  const cells=new Map(),fallback=[],boxed=[]
  for(const e of entities){
    const b=entityBBox(e)
    if(b&&b.every(Number.isFinite))boxed.push([e,b]);else fallback.push(e)
  }
  if(!boxed.length)return {cells,cellSize:1,fallback}
  const cellSize=deriveCellSize(boxed)
  for(const[e,[bx0,by0,bx1,by1]]of boxed){
    const cx0=Math.floor(bx0/cellSize),cx1=Math.floor(bx1/cellSize)
    const cy0=Math.floor(by0/cellSize),cy1=Math.floor(by1/cellSize)
    if((cx1-cx0+1)*(cy1-cy0+1)>MAX_CELLS_PER_ENTITY){fallback.push(e);continue}
    for(let cx=cx0;cx<=cx1;cx++)for(let cy=cy0;cy<=cy1;cy++){
      const k=`${cx},${cy}`;if(!cells.has(k))cells.set(k,[]);cells.get(k).push(e)
    }
  }
  return {cells,cellSize,fallback}
}

export function entitiesInBox(manifestOrIndex,x1,y1,x2,y2,mode='crossing'){
  const minX=Math.min(x1,x2),maxX=Math.max(x1,x2),minY=Math.min(y1,y2),maxY=Math.max(y1,y2)
  // Accept either a plain manifest array or a spatial index from buildSpatialIndex
  let candidates
  if(manifestOrIndex&&!Array.isArray(manifestOrIndex)&&manifestOrIndex.cells){
    const idx=manifestOrIndex
    const cx0=Math.floor(minX/idx.cellSize),cx1=Math.floor(maxX/idx.cellSize)
    const cy0=Math.floor(minY/idx.cellSize),cy1=Math.floor(maxY/idx.cellSize)
    const span=(cx1-cx0+1)*(cy1-cy0+1)
    const seen=new Set();candidates=[]
    const push=e=>{if(!seen.has(e)){seen.add(e);candidates.push(e)}}
    // A box drawn far outside the drawing spans more cells than the grid holds — sweeping every
    // occupied cell is then both cheaper and immune to a runaway loop over empty space.
    if(!Number.isFinite(span)||span>idx.cells.size){
      for(const bucket of idx.cells.values())for(const e of bucket)push(e)
    } else {
      for(let cx=cx0;cx<=cx1;cx++)for(let cy=cy0;cy<=cy1;cy++)
        for(const e of(idx.cells.get(`${cx},${cy}`)||[]))push(e)
    }
    for(const e of idx.fallback)push(e)
  } else {
    candidates=manifestOrIndex
  }
  const ptIn=(x,y)=>x>=minX&&x<=maxX&&y>=minY&&y<=maxY
  const boxCorners=[[minX,minY],[maxX,minY],[maxX,maxY],[minX,maxY]]
  const boxEdges=boxCorners.map((c,i)=>[c,boxCorners[(i+1)%4]])
  const segCrossesBox=(a,b)=>boxEdges.some(([c,d])=>segIntersect(a,b,c,d))
  const polySegsHitBox=(pts,closed)=>{
    for(let i=0;i+1<pts.length;i++)if(segCrossesBox(pts[i],pts[i+1]))return true
    if(closed&&pts.length>2)if(segCrossesBox(pts[pts.length-1],pts[0]))return true
    return false
  }
  const polyHitsBox=(pts,closed=false)=>
    pts.some(([x,y])=>ptIn(x,y)) ||
    polySegsHitBox(pts,closed) ||
    (closed&&pts.length>2&&boxCorners.some(([bx,by])=>ptInPoly(bx,by,pts)))
  const r=new Set()
  if(mode==='window'){
    for(const e of candidates){
      if(e.type==='LINE'&&e.start&&e.end){
        if(ptIn(e.start[0],e.start[1])&&ptIn(e.end[0],e.end[1]))r.add(e.handle)
      } else if(e.type==='CIRCLE'&&e.center){
        const[cx,cy]=e.center,rad=e.radius||0
        if(cx-rad>=minX&&cx+rad<=maxX&&cy-rad>=minY&&cy+rad<=maxY)r.add(e.handle)
      } else if(e.type==='LWPOLYLINE'&&e.points?.length){
        if(tessellatePolyline(e.points,e.bulge,e.closed).every(([x,y])=>ptIn(x,y)))r.add(e.handle)
      } else if(e.position){
        if(ptIn(e.position[0],e.position[1]))r.add(e.handle)
      }
    }
  } else {
    for(const e of candidates){
      if(e.type==='LINE'&&e.start&&e.end){
        if(ptIn(e.start[0],e.start[1])||ptIn(e.end[0],e.end[1])||segCrossesBox([e.start[0],e.start[1]],[e.end[0],e.end[1]]))r.add(e.handle)
      } else if(e.type==='CIRCLE'&&e.center){
        const[cx,cy]=e.center,rad=e.radius||0,rad2=rad*rad
        const outside=([px,py])=>(px-cx)**2+(py-cy)**2>=rad2
        const edgeHits=boxEdges.some(([a,b])=>segIntersectsCircle(a,b,cx,cy,rad)&&(outside(a)||outside(b)))
        const boxInsideCircle=boxCorners.some(([px,py])=>(px-cx)**2+(py-cy)**2<=rad2)
        if(ptIn(cx,cy)||edgeHits||boxInsideCircle)r.add(e.handle)
      } else if(e.type==='LWPOLYLINE'&&e.points?.length){
        if(polyHitsBox(tessellatePolyline(e.points,e.bulge,e.closed),e.closed))r.add(e.handle)
      } else if(e.position){
        if(ptIn(e.position[0],e.position[1]))r.add(e.handle)
      }
    }
  }
  return r
}

// ── Geometry similarity ────────────────────────────────────────────────────────

export function _polylineArea(points) {
  let a = 0
  for (let i = 0; i < points.length; i++) {
    const [x1,y1] = points[i], [x2,y2] = points[(i+1)%points.length]
    a += x1*y2 - x2*y1
  }
  return Math.abs(a) / 2
}

function _edgeLengths(points, closed) {
  const lens = []
  for (let i = 0; i+1 < points.length; i++)
    lens.push(Math.hypot(points[i+1][0]-points[i][0], points[i+1][1]-points[i][1]))
  if (closed && points.length > 1)
    lens.push(Math.hypot(points[0][0]-points[points.length-1][0], points[0][1]-points[points.length-1][1]))
  return lens
}

export function getGeomSignature(entity) {
  if (!entity) return null
  if (entity.type === 'CIRCLE') {
    return { type: 'CIRCLE', radius: entity.radius }
  }
  if (entity.type === 'LINE' && entity.start && entity.end) {
    const dx = entity.end[0] - entity.start[0], dy = entity.end[1] - entity.start[1]
    const length = Math.hypot(dx, dy)
    // Undirected angle bucket: 0=horizontal, 1=+45°, 2=vertical, 3=-45°
    const normDeg = ((Math.atan2(dy, dx) * 180 / Math.PI % 180) + 180) % 180
    const angleBucket = Math.round(normDeg / 45) % 4
    return { type: 'LINE', length, angleBucket }
  }
  if (entity.type === 'LWPOLYLINE' && entity.points?.length >= 2) {
    let pts = entity.points
    // Legacy POLYLINE entities are serialized with the closing point repeated
    // (server/utils.py); drop it so identical shapes hash the same regardless
    // of source entity type.
    if (entity.closed && pts.length > 2) {
      const [fx, fy] = pts[0], [lx, ly] = pts[pts.length - 1]
      if (Math.hypot(fx - lx, fy - ly) < 1e-9) pts = pts.slice(0, -1)
    }
    const edges = _edgeLengths(pts, entity.closed)
    const sortedEdges = [...edges].sort((a,b) => a-b)
    const area = entity.closed && pts.length >= 3 ? _polylineArea(pts) : 0
    const perimeter = edges.reduce((a,b) => a+b, 0)
    // Bounding box aspect ratio (scale-invariant shape descriptor)
    let minX=Infinity,maxX=-Infinity,minY=Infinity,maxY=-Infinity
    for (const [x,y] of pts) {
      if(x<minX)minX=x; if(x>maxX)maxX=x; if(y<minY)minY=y; if(y>maxY)maxY=y
    }
    const bboxW = Math.max(maxX-minX, 0.001), bboxH = Math.max(maxY-minY, 0.001)
    const aspect = Math.max(bboxW,bboxH) / Math.min(bboxW,bboxH)
    return { type: 'LWPOLYLINE', vertexCount: pts.length, closed: !!entity.closed, points: pts, sortedEdges, area, perimeter, aspect }
  }
  return null
}

function _sigsMatch(s1, s2, tol) {
  if (!s1 || !s2 || s1.type !== s2.type) return false
  const pct = (a, b) => { const r = (a+b)/2||1; return Math.abs(a-b)/r }
  if (s1.type === 'CIRCLE') return pct(s1.radius, s2.radius) <= tol
  if (s1.type === 'LINE')   return s1.angleBucket === s2.angleBucket && pct(s1.length, s2.length) <= tol
  if (s1.type === 'LWPOLYLINE') {
    if (s1.closed !== s2.closed) return false
    // Perimeter must match (catches scale-mismatches early)
    if (pct(s1.perimeter, s2.perimeter) > tol * 2) return false
    // Aspect ratio must match (shape invariant)
    if (pct(s1.aspect, s2.aspect) > tol * 3) return false
    // Area must match for closed shapes
    if (s1.closed && s1.area > 0 && s2.area > 0 && pct(s1.area, s2.area) > tol * 2) return false
    // Vertex count must match — a triangle should never match a rectangle
    if (s1.vertexCount !== s2.vertexCount) return false
    // Precise edge-length check (sorted edges are rotation-invariant)
    for (let i = 0; i < s1.sortedEdges.length; i++)
      if (pct(s1.sortedEdges[i], s2.sortedEdges[i]) > tol) return false
    return true
  }
  return false
}

export function findSimilarEntities(targetEntities, pool, tol = 0.05) {
  if (!targetEntities?.length || !pool?.length) return new Set()
  const targetHandles = new Set(targetEntities.map(e => e.handle))
  const targetSigs = targetEntities.map(getGeomSignature).filter(Boolean)
  if (!targetSigs.length) return new Set()
  const result = new Set()
  for (const e of pool) {
    if (targetHandles.has(e.handle)) continue
    const eSig = getGeomSignature(e)
    if (eSig && targetSigs.some(s => _sigsMatch(s, eSig, tol))) result.add(e.handle)
  }
  return result
}

// Round v to 5 significant figures → string, for use in hash keys.
// 4 figures gave ~0.05% worst-case resolution near a decade boundary
// (e.g. 1.0 and 1.0004 collided) — 5 figures keeps it within ~0.01%.
function _sig5(v) {
  if (v === 0) return '0'
  const d = Math.floor(Math.log10(Math.abs(v))) + 1
  const f = Math.pow(10, 5 - d)
  return String(Math.round(v * f) / f)
}

function _turnAngleDeg(a, b) {
  const cross = a[0] * b[1] - a[1] * b[0]
  const dot = a[0] * b[0] + a[1] * b[1]
  return Math.atan2(cross, dot) * 180 / Math.PI
}

// Edge lengths and the turn angle between each consecutive pair of edges,
// interleaved in order. Edge lengths alone (sorted or not) don't determine
// a polyline's shape — e.g. "right, up, right" and "right, right, up" have
// the same edges but are different paths — the turn angles are what
// distinguish them.
function _shapeTokens(points, closed) {
  const pts = closed ? [...points, points[0]] : points
  const dirs = []
  for (let i = 0; i + 1 < pts.length; i++) dirs.push([pts[i+1][0]-pts[i][0], pts[i+1][1]-pts[i][1]])
  const edges = dirs.map(([dx, dy]) => Math.hypot(dx, dy))
  const tokens = [_sig5(edges[0])]
  for (let i = 1; i < edges.length; i++) {
    tokens.push(_sig5(_turnAngleDeg(dirs[i-1], dirs[i])))
    tokens.push(_sig5(edges[i]))
  }
  if (closed) tokens.push(_sig5(_turnAngleDeg(dirs[dirs.length - 1], dirs[0])))
  return tokens.join(',')
}

// Canonical shape key: tries every rotation (a closed polygon can be listed
// starting at any vertex) and both traversal directions, keeping the
// lexicographically smallest result so equivalent listings of the same
// shape produce the same string.
function _canonicalShapeKey(points, closed) {
  const variants = []
  const rotations = closed ? points.length : 1
  for (let r = 0; r < rotations; r++) {
    const rotated = [...points.slice(r), ...points.slice(0, r)]
    variants.push(_shapeTokens(rotated, closed))
    variants.push(_shapeTokens([...rotated].reverse(), closed))
  }
  return variants.sort()[0]
}

// Compact shape key — identical geometry (within ~0.01% rounding) produces the same key
export function sigKey(entity) {
  const sig = getGeomSignature(entity)
  if (!sig) return null
  if (sig.type === 'LINE')       return `L:${_sig5(sig.length)},${sig.angleBucket}`
  if (sig.type === 'CIRCLE')     return `C:${_sig5(sig.radius)}`
  if (sig.type === 'LWPOLYLINE') return `P${sig.vertexCount}:${_canonicalShapeKey(sig.points, sig.closed)}`
  return null
}

// Pass 1: entities whose shape key exactly matches any target's key
export function findExactMatches(targetEntities, pool) {
  if (!targetEntities?.length || !pool?.length) return new Set()
  const targetHandles = new Set(targetEntities.map(e => e.handle))
  const targetKeys = new Set(targetEntities.map(sigKey).filter(Boolean))
  if (!targetKeys.size) return new Set()
  const result = new Set()
  for (const e of pool) {
    if (targetHandles.has(e.handle)) continue
    const k = sigKey(e)
    if (k && targetKeys.has(k)) result.add(e.handle)
  }
  return result
}

export function moveEntity(e,dx,dy){
  if (e.type==='LINE'&&e.start&&e.end) return{...e,start:[e.start[0]+dx,e.start[1]+dy],end:[e.end[0]+dx,e.end[1]+dy]}
  if (e.type==='CIRCLE'&&e.center) return{...e,center:[e.center[0]+dx,e.center[1]+dy]}
  if (e.type==='LWPOLYLINE'&&e.points) return{...e,points:e.points.map(([x,y])=>[x+dx,y+dy])}
  return e
}
function rotatePt(p, cx, cy, a) {
  const cos = Math.cos(a), sin = Math.sin(a), dx = p[0]-cx, dy = p[1]-cy
  return [cx + dx*cos - dy*sin, cy + dx*sin + dy*cos]
}
export function rotateEntity(e, cx, cy, angle) {
  if (e.type==='LINE'&&e.start&&e.end) return{...e,start:rotatePt(e.start,cx,cy,angle),end:rotatePt(e.end,cx,cy,angle)}
  if (e.type==='CIRCLE'&&e.center) return{...e,center:rotatePt(e.center,cx,cy,angle)}
  if (e.type==='LWPOLYLINE'&&e.points) return{...e,points:e.points.map(p=>rotatePt(p,cx,cy,angle))}
  return e
}
function mirrorPt(p, ax, ay, bx, by) {
  const dx = bx-ax, dy = by-ay, lenSq = dx*dx+dy*dy
  if (!lenSq) return [...p]
  const t = ((p[0]-ax)*dx + (p[1]-ay)*dy) / lenSq
  return [2*(ax+t*dx)-p[0], 2*(ay+t*dy)-p[1]]
}
export function mirrorEntity(e, ax, ay, bx, by) {
  if (e.type==='LINE'&&e.start&&e.end) return{...e,start:mirrorPt(e.start,ax,ay,bx,by),end:mirrorPt(e.end,ax,ay,bx,by)}
  if (e.type==='CIRCLE'&&e.center) return{...e,center:mirrorPt(e.center,ax,ay,bx,by)}
  if (e.type==='LWPOLYLINE'&&e.points) return{...e,points:e.points.map(p=>mirrorPt(p,ax,ay,bx,by)),...(e.bulge&&{bulge:e.bulge.map(b=>(b?-b:0))})}
  return e
}
function segIntersect(p1,p2,p3,p4){
  const[x1,y1]=p1,[x2,y2]=p2,[x3,y3]=p3,[x4,y4]=p4
  const d=(x1-x2)*(y3-y4)-(y1-y2)*(x3-x4)
  if(!d)return false
  const t=((x1-x3)*(y3-y4)-(y1-y3)*(x3-x4))/d
  const u=-((x1-x2)*(y1-y3)-(y1-y2)*(x1-x3))/d
  return t>=0&&t<=1&&u>=0&&u<=1
}
// ── Door Q-Line auto-detection ────────────────────────────────────────────────
// Reads the tagged (classified) manifest and emits one Door Q-Line per door.
// The Q-line spans the door opening (jamb-to-jamb), providing the exact barrier
// needed by the flood tool without introducing door-arc geometry artefacts.
//
// Detection uses the geometric structure of standard CAD door symbols:
//   • A LINE entity  = door leaf in the open position (hinge → open tip)
//   • A LWPOLYLINE   = quarter-circle swing indicator (open tip → closed tip)
//   The swing arc's circumcenter IS the hinge.  Q-line = hinge → closed tip.
//
// Strategies (tried in order, most geometrically precise first):
//   1. LINE + ARC  — arc circumcenter gives exact hinge; works for any rotation
//   2. ARC pairs   — two arcs → two hinges → double-door opening
//   3. ARC only    — arc terminals directly span the opening
//   4. LINE pairs  — two leaves whose tips meet at opening centre (double door)
//   5. Single LINE — simplified symbol with no swing indicator
//   6. Widest terminal span — last-resort heuristic
export function generateDoorQLines(manifest) {
  const MIN_WIDTH    = 40   // cm — minimum door opening (covers small doors)
  const MAX_WIDTH    = 450  // cm — maximum opening (generous for double doors)
  const CLUSTER_DIST = 50   // cm — entity-grouping radius (tolerates CAD gaps)
  const DUP_TOL      = 8    // cm — skip if a Q-line already exists this close
  const ARC_MIN_PTS  = 4    // minimum points to attempt circle-fit detection

  // ── Layer helpers ─────────────────────────────────────────────────────────
  const isDoorLayer = (e) => {
    const lay  = (e.layer || '').toLowerCase()
    const orig = (e.original_layer_name || '').toLowerCase()
    return (lay === 'doors' || lay === 'qbiq_doors' ||
            orig === 'doors' || orig === 'qbiq_doors') &&
           !lay.includes('q-line') && !lay.includes('q_line') &&
           lay !== 'door lines' && lay !== 'qbiq_door_lines'
  }
  const isQLineLayer = (e) => {
    const lay = (e.layer || '').toLowerCase()
    return lay.includes('q-line') || lay.includes('q_line') ||
           lay === 'door lines' || lay === 'qbiq_door_lines'
  }

  // ── Geometry helpers ──────────────────────────────────────────────────────

  // First and last point of an entity (the two "anchor" points for clustering)
  function terminalPts(e) {
    if (e.type === 'LWPOLYLINE') {
      const pts = e.points.filter(p => p?.length >= 2)
      if (pts.length < 2) return []
      return [pts[0], pts[pts.length - 1]]
    }
    if (e.type === 'LINE') return [e.start, e.end]
    return []
  }

  // Circumcenter of three 2-D points = center of the unique circle through them.
  // Returns null for collinear points.
  function circumcenter(p1, p2, p3) {
    const ax = p1[0], ay = p1[1]
    const bx = p2[0], by = p2[1]
    const cx = p3[0], cy = p3[1]
    const D = 2 * (ax*(by-cy) + bx*(cy-ay) + cx*(ay-by))
    if (Math.abs(D) < 1e-10) return null
    const ux = ((ax*ax+ay*ay)*(by-cy) + (bx*bx+by*by)*(cy-ay) + (cx*cx+cy*cy)*(ay-by)) / D
    const uy = ((ax*ax+ay*ay)*(cx-bx) + (bx*bx+by*by)*(ax-cx) + (cx*cx+cy*cy)*(bx-ax)) / D
    return [ux, uy]
  }

  // If an LWPOLYLINE is a circular arc, return { center, radius }; else null.
  // "Circular" means all points are within 15 % of the mean radius from the
  // fitted circumcenter — generous enough to tolerate arc approximation noise.
  function detectArc(pts) {
    if (pts.length < ARC_MIN_PTS) return null
    const mid = Math.floor(pts.length / 2)
    const c = circumcenter(pts[0], pts[mid], pts[pts.length - 1])
    if (!c) return null
    const dists = pts.map(p => Math.hypot(p[0]-c[0], p[1]-c[1]))
    const mean  = dists.reduce((a,b) => a+b, 0) / dists.length
    if (mean < 1) return null
    const maxDev = dists.reduce((m,d) => Math.max(m, Math.abs(d - mean)), 0)
    if (maxDev / mean > 0.15) return null
    return { center: c, radius: mean }
  }

  // ── Clustering ────────────────────────────────────────────────────────────
  // Union-find: join entities whose terminal points are within CLUSTER_DIST.
  function buildClusters(entities) {
    const n = entities.length
    const parent = Array.from({ length: n }, (_, i) => i)
    function find(i) { return parent[i] === i ? i : (parent[i] = find(parent[i])) }
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const pi = terminalPts(entities[i])
        const pj = terminalPts(entities[j])
        if (pi.some(a => pj.some(b => Math.hypot(a[0]-b[0], a[1]-b[1]) <= CLUSTER_DIST)))
          parent[find(i)] = find(j)
      }
    }
    const map = new Map()
    for (let i = 0; i < n; i++) {
      const r = find(i)
      if (!map.has(r)) map.set(r, [])
      map.get(r).push(entities[i])
    }
    return [...map.values()]
  }

  // ── Q-line for one cluster ────────────────────────────────────────────────
  function clusterQLine(entities) {
    // All LINE entities in this cluster (no pre-filtering by length — small doors included)
    const lines = entities.filter(e => e.type === 'LINE' && e.start && e.end)

    // Polylines separated into circular arcs and non-arc polys
    const arcEntries = []
    for (const e of entities) {
      if (e.type !== 'LWPOLYLINE' || !e.points?.length) continue
      const arc = detectArc(e.points)
      if (arc) arcEntries.push({ e, arc })
    }

    // ── Strategy 1: LINE leaf + swing ARC ─────────────────────────────────
    // The arc's circumcenter IS the hinge (pivot point).
    // Q-line = hinge → closed-position tip of the arc.
    //
    // Validation: LINE length ≈ arc radius (both equal the door leaf width).
    for (const line of lines) {
      const leafLen = Math.hypot(line.end[0]-line.start[0], line.end[1]-line.start[1])
      if (leafLen < MIN_WIDTH * 0.5 || leafLen > MAX_WIDTH) continue

      for (const { e: arcEnt, arc } of arcEntries) {
        const { center: ctr, radius } = arc

        // Leaf length must be within 30 % of the arc radius (both = door width)
        if (Math.abs(leafLen - radius) / radius > 0.30) continue

        // Identify which LINE endpoint is the hinge (closest to arc center)
        const hingeTol = Math.max(10, radius * 0.20)  // ≤ 20 % of door width
        const dS = Math.hypot(line.start[0]-ctr[0], line.start[1]-ctr[1])
        const dE = Math.hypot(line.end[0]  -ctr[0], line.end[1]  -ctr[1])
        let hinge, openTip
        if      (dS <= hingeTol && dS <= dE) { hinge = line.start; openTip = line.end   }
        else if (dE <= hingeTol)              { hinge = line.end;   openTip = line.start }
        else continue

        // Of the two arc terminals, the one farther from the open-tip = closed position
        const pts = arcEnt.points
        const t0 = pts[0], tN = pts[pts.length - 1]
        const closedTip = Math.hypot(openTip[0]-t0[0], openTip[1]-t0[1]) >
                          Math.hypot(openTip[0]-tN[0], openTip[1]-tN[1]) ? t0 : tN

        const opening = Math.hypot(closedTip[0]-hinge[0], closedTip[1]-hinge[1])
        if (opening >= MIN_WIDTH && opening <= MAX_WIDTH) return [hinge, closedTip]
      }
    }

    // ── Strategy 2: Double door — two swing ARCs ───────────────────────────
    // Each arc's circumcenter is one hinge.  Q-line = hinge₁ → hinge₂.
    // Validated by confirming each arc has a terminal near the opening mid-point
    // (the two closed tips should face each other at the centre of the opening).
    if (arcEntries.length >= 2) {
      for (let i = 0; i < arcEntries.length; i++) {
        for (let j = i + 1; j < arcEntries.length; j++) {
          const hi = arcEntries[i].arc.center
          const hj = arcEntries[j].arc.center
          const span = Math.hypot(hi[0]-hj[0], hi[1]-hj[1])
          if (span < MIN_WIDTH * 1.5 || span > MAX_WIDTH) continue

          // Verify: each arc should have one terminal close to the opening midpoint
          const mid = [(hi[0]+hj[0])/2, (hi[1]+hj[1])/2]
          const ptsI = arcEntries[i].e.points, ptsJ = arcEntries[j].e.points
          const hasCloseI = [ptsI[0], ptsI[ptsI.length-1]].some(
            p => Math.hypot(p[0]-mid[0], p[1]-mid[1]) < span * 0.40)
          const hasCloseJ = [ptsJ[0], ptsJ[ptsJ.length-1]].some(
            p => Math.hypot(p[0]-mid[0], p[1]-mid[1]) < span * 0.40)
          if (hasCloseI && hasCloseJ) return [hi, hj]
        }
      }
    }

    // ── Strategy 3: ARC only (simplified door — no separate leaf LINE) ─────
    // The two arc terminals directly span the door opening.
    for (const { e: arcEnt } of arcEntries) {
      const pts = arcEnt.points
      const t0 = pts[0], tN = pts[pts.length - 1]
      const span = Math.hypot(t0[0]-tN[0], t0[1]-tN[1])
      if (span >= MIN_WIDTH && span <= MAX_WIDTH) return [t0, tN]
    }

    // ── Strategy 4: Double door — two LINEs whose tips meet at centre ──────
    // Both leaves pivot from opposite jambs and open toward each other.
    // Q-line = hinge₁ → hinge₂ (the far ends, which are at the wall faces).
    if (lines.length >= 2) {
      for (let i = 0; i < lines.length; i++) {
        for (let j = i + 1; j < lines.length; j++) {
          const L1 = lines[i], L2 = lines[j]
          const combos = [
            [L1.start, L1.end, L2.start, L2.end],
            [L1.start, L1.end, L2.end,   L2.start],
            [L1.end,   L1.start, L2.start, L2.end],
            [L1.end,   L1.start, L2.end,   L2.start],
          ]
          for (const [h1, tip1, tip2, h2] of combos) {
            // Tips must meet (or nearly meet) at the opening centre
            if (Math.hypot(tip1[0]-tip2[0], tip1[1]-tip2[1]) > CLUSTER_DIST * 1.5) continue
            const w = Math.hypot(h1[0]-h2[0], h1[1]-h2[1])
            if (w >= MIN_WIDTH * 1.5 && w <= MAX_WIDTH) return [h1, h2]
          }
        }
      }
    }

    // ── Strategy 5: Single LINE spanning the opening ───────────────────────
    // Some simplified blocks use only a straight line with no arc.
    // Take the longest LINE within bounds — most likely to be the actual leaf.
    let bestLine = null, bestLineLen = 0
    for (const line of lines) {
      const len = Math.hypot(line.end[0]-line.start[0], line.end[1]-line.start[1])
      if (len >= MIN_WIDTH && len <= MAX_WIDTH && len > bestLineLen) {
        bestLine = line; bestLineLen = len
      }
    }
    if (bestLine) return [bestLine.start, bestLine.end]

    // ── Strategy 6: Widest terminal-point pair within bounds (last resort) ──
    // Catches any remaining symbols whose structure we could not classify above.
    const allTerms = entities.flatMap(e => terminalPts(e))
    let bestDist = 0, bestPair = null
    for (let i = 0; i < allTerms.length; i++) {
      for (let j = i + 1; j < allTerms.length; j++) {
        const d = Math.hypot(allTerms[i][0]-allTerms[j][0], allTerms[i][1]-allTerms[j][1])
        if (d >= MIN_WIDTH && d <= MAX_WIDTH && d > bestDist) {
          bestDist = d; bestPair = [allTerms[i], allTerms[j]]
        }
      }
    }
    return bestPair
  }

  // ── Existing Q-line dedup ─────────────────────────────────────────────────
  const existingQLines = manifest.filter(e =>
    isQLineLayer(e) && e.isUserDrawn &&
    (e.type === 'LWPOLYLINE' || e.type === 'LINE') &&
    (e.points?.length >= 2 || (e.start && e.end))
  )
  function qlineExists(p1, p2) {
    return existingQLines.some(q => {
      const a = q.points ? q.points[0] : q.start
      const b = q.points ? q.points[q.points.length - 1] : q.end
      if (!a || !b) return false
      return (Math.hypot(a[0]-p1[0], a[1]-p1[1]) < DUP_TOL &&
              Math.hypot(b[0]-p2[0], b[1]-p2[1]) < DUP_TOL) ||
             (Math.hypot(a[0]-p2[0], a[1]-p2[1]) < DUP_TOL &&
              Math.hypot(b[0]-p1[0], b[1]-p1[1]) < DUP_TOL)
    })
  }

  // ── Main pass ─────────────────────────────────────────────────────────────
  const doorEntities = manifest.filter(e => isDoorLayer(e) && e.type && (
    (e.type === 'LWPOLYLINE' && e.points?.length >= 2) ||
    (e.type === 'LINE' && e.start?.length >= 2 && e.end?.length >= 2)
  ))
  if (!doorEntities.length) return []

  const clusters = buildClusters(doorEntities)
  const qlines = []
  const ts = Date.now()

  for (const cluster of clusters) {
    const pair = clusterQLine(cluster)
    if (!pair) continue
    const [p1, p2] = pair
    if (qlineExists(p1, p2)) continue
    qlines.push({
      type: 'LWPOLYLINE',
      layer: 'Door Q-Line',
      original_layer_name: 'Door Q-Line',
      points: [p1, p2],
      closed: false,
      isUserDrawn: true,
      handle: `dql-${ts}-${qlines.length}`,
    })
  }

  return qlines
}

function segIntersectsCircle(p1,p2,cx,cy,r){
  const dx=p2[0]-p1[0],dy=p2[1]-p1[1]
  const lenSq=dx*dx+dy*dy
  if(!lenSq)return Math.hypot(p1[0]-cx,p1[1]-cy)<=r
  const t=Math.max(0,Math.min(1,((cx-p1[0])*dx+(cy-p1[1])*dy)/lenSq))
  return Math.hypot(p1[0]+t*dx-cx,p1[1]+t*dy-cy)<=r
}

// Focus and shift-select rows in the classified view are classes, so they match the entity's class only.
// Matching the source layer name too made focus on class "0" pick up entities on the DXF layer called "0".
export const inFocusedClass = (e, key, av) => normLayer(e.layer) === key || (av !== 'classified' && layerVisibilityKey(e, av) === key)
export const inShiftSelection = (e, selected) => selected.has(normLayer(e.layer))
